import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Card,
  Title,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Loader,
  Group,
  Flex,
  TextInput,
  Button,
  Box,
  Badge,
  SimpleGrid,
  Select,
} from '@mantine/core';
import {
  Users,
  User,
  Search,
  Layers,
  Plus,
  Calendar,
  Clock,
  Link,
} from 'react-feather';
import { ButtonToggle, type ButtonToggleOption } from '../../../components/common';
import { coachPortalApi, type CoachCohortDetail, type CoachWorkshop } from '../../../services/api/coachPortal';
import type { WorkshopWithSession } from '../../../services/api/workshops';
import type { CoachHuddle, OneOnOneParticipant, OneOnOneSessionSlot, Session } from '../../../types';
import { isCheckinOneOnOneType } from '../../../types';
import { GroupSessionsCard } from './GroupSessionsCard';
import { GroupBookingLinks } from './GroupBookingLinks';
import { CoachCohortEventsSection } from './CoachCohortEventsSection';
import { sessionsApi } from '../../../services/api/sessions';
import { useAuthStore } from '../../../stores/auth';
import { notifications } from '@mantine/notifications';
import {
  OneOnOneCoachingSection,
  OneOnOneSessionDetailDrawer,
  WorkshopDetailDrawer,
  GroupCoachingBySessionView,
  OneOnOneBySessionView,
  type OneOnOneViewMode,
} from '../../cohorts/components/sessions';
import { EditSessionModal } from '../../cohorts/components/EditSessionModal';
import { formatDate, formatTime } from '../../../utils/formatters';

type SectionFilter = 'all' | 'groupCoaching' | 'workshops' | 'oneOnOnes';

export interface SessionsTabContentProps {
  cohort: CoachCohortDetail;
  onSessionClick: (sessionId: string) => void;
  huddles: CoachHuddle[];
  huddlesLoading: boolean;
  onAddHuddle: () => void;
  onHuddleClick: (huddleId: string) => void;
  onEditHuddle: (huddle: CoachHuddle) => void;
  onCancelHuddle: (huddle: CoachHuddle) => void;
  onOpenSurveyResults?: (
    groupId: string,
    groupName: string,
    tab?: 'post_session' | 'midpoint' | 'endpoint',
  ) => void;
  onOpenCoachFeedback?: (surveyType: 'midpoint' | 'endpoint') => void;
  isPartnerCoach?: boolean;
  /**
   * MED-1360: a 1:1 session to open in the 1:1 drawer, from the attendance
   * banner or the dashboard card. A 1:1 must never open the group attendance
   * drawer: that form starts every participant on Attended, and a mark
   * triggers the coach payment.
   */
  openOneOnOneSessionId?: string | null;
  /** MED-1360: called once openOneOnOneSessionId is handled. */
  onOneOnOneSessionOpened?: () => void;
  /** MED-1360: the 1:1 drawer saved or cleared attendance (refreshes the banner). */
  onOneOnOneAttendanceChanged?: () => void;
}

// --- Workshop status badge (same as admin) ---
function WorkshopStatusBadge({ workshop }: { workshop: CoachWorkshop }) {
  const status = workshop.session_status;
  if (!status || status === 'unscheduled') {
    return (
      <Badge size="xs" color="gray" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Draft
      </Badge>
    );
  }
  if (status === 'scheduled') {
    return (
      <Badge size="xs" color="blue" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Scheduled
      </Badge>
    );
  }
  if (status === 'in_progress') {
    return (
      <Badge size="xs" color="dangerRed" variant="filled" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        In Progress
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge size="xs" color="medleyGreen" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Completed
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge size="xs" color="dangerRed" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Cancelled
      </Badge>
    );
  }
  return (
    <Badge size="xs" color="gray" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
      {status}
    </Badge>
  );
}

// --- Coach workshop card (read-only, no Edit button) ---
function CoachWorkshopCard({
  workshop,
  onClick,
}: {
  workshop: CoachWorkshop;
  onClick: (workshop: CoachWorkshop) => void;
}) {
  const isConfigured = !!workshop.scheduled_start;
  const hasParticipants = (workshop.participant_count ?? 0) > 0;

  return (
    <Box
      onClick={() => onClick(workshop)}
      style={{
        border: '1px solid #E8E6E1',
        borderRadius: 8,
        padding: '16px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        width: '100%',
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      <Stack gap="sm">
        {/* Header: title + status badge */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" style={{ flex: 1 }} lineClamp={1}>
              {workshop.title}
            </Text>
            <WorkshopStatusBadge workshop={workshop} />
          </Group>
        </Group>

        {/* Date/time */}
        <Group gap="xs" wrap="nowrap">
          <Calendar size={13} color={isConfigured ? '#444' : '#aaa'} />
          {isConfigured ? (
            <Text size="xs" c="dark">
              {formatDate(workshop.scheduled_start!)} · {formatTime(workshop.scheduled_start!)}
            </Text>
          ) : (
            <Text size="xs" c="dimmed" fs="italic">
              No date set
            </Text>
          )}
        </Group>

        {/* Duration */}
        {workshop.session_duration_minutes && (
          <Group gap="xs" wrap="nowrap">
            <Clock size={13} color="#888" />
            <Text size="xs" c="dimmed">
              {workshop.session_duration_minutes} min
            </Text>
          </Group>
        )}

        {/* Meeting link */}
        {workshop.zoom_link && (
          <Group gap="xs" wrap="nowrap">
            <Link size={13} color="#888" />
            <Text
              size="xs"
              c="blue"
              component="a"
              href={workshop.zoom_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              onClick={(e) => e.stopPropagation()}
            >
              Meeting link
            </Text>
          </Group>
        )}

        {/* Participant count */}
        <Group gap="xs" wrap="nowrap">
          <Users size={13} color="#888" />
          {hasParticipants ? (
            <Text size="xs" c="dimmed">
              {workshop.participant_count} participant{workshop.participant_count !== 1 ? 's' : ''}
            </Text>
          ) : (
            <Text size="xs" c="dimmed" fs="italic">
              No participants assigned
            </Text>
          )}
        </Group>
      </Stack>
    </Box>
  );
}

export function SessionsTabContent({
  cohort,
  onSessionClick,
  huddles,
  huddlesLoading,
  onAddHuddle,
  onHuddleClick,
  onEditHuddle,
  onCancelHuddle,
  onOpenSurveyResults,
  onOpenCoachFeedback,
  isPartnerCoach = false,
  openOneOnOneSessionId = null,
  onOneOnOneSessionOpened,
  onOneOnOneAttendanceChanged,
}: SessionsTabContentProps) {
  const { user } = useAuthStore();

  // Section filter state
  const [activeFilter, setActiveFilter] = useState<SectionFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // MED-702 AC4: "View by Date" / "View by Session" toggle, read-only data
  // in coach view. In-memory only — resets on page reload.
  const [groupingMode, setGroupingMode] = useState<'date' | 'session'>('date');

  // MED-1360: the 1:1 list or card choice lives here, as in the admin view.
  // The section unmounts when the filter hides it, which reset the choice.
  const [oneOnOneViewMode, setOneOnOneViewMode] = useState<OneOnOneViewMode>('list');

  // Accordion state - all groups expanded by default
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(cohort.groups.map((g) => g.id)),
  );

  // E18-3: 1:1 participants (not applicable for partner coaches)
  const [oneOnOneParticipants, setOneOnOneParticipants] = useState<OneOnOneParticipant[]>([]);
  const [oneOnOneLoading, setOneOnOneLoading] = useState(false);
  // MED-1360: true once the first load finished, so a request to open a 1:1
  // waits for the list instead of reading the empty start state.
  const [oneOnOneLoaded, setOneOnOneLoaded] = useState(false);

  // E18-3: 1:1 session detail drawer + edit modal state
  const [selectedOneOnOneParticipant, setSelectedOneOnOneParticipant] = useState<OneOnOneParticipant | null>(null);
  const [selectedOneOnOneSessionId, setSelectedOneOnOneSessionId] = useState<string | null>(null);
  const [oneOnOneDetailOpened, setOneOnOneDetailOpened] = useState(false);
  // MED-1360: the drawer was opened to mark attendance. A partner coach has
  // no 1:1 drawer otherwise, but must mark their own check-in here.
  const [oneOnOneOpenedForAttendance, setOneOnOneOpenedForAttendance] = useState(false);
  const [editOneOnOneSession, setEditOneOnOneSession] = useState<{ session: Session; participant: OneOnOneParticipant } | null>(null);

  // MED-626: Workshop state
  const [workshops, setWorkshops] = useState<CoachWorkshop[]>([]);
  const [workshopsLoading, setWorkshopsLoading] = useState(false);
  const [detailWorkshop, setDetailWorkshop] = useState<CoachWorkshop | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const loadOneOnOneParticipants = useCallback(async () => {
    if (!user?.id) return;
    setOneOnOneLoading(true);
    try {
      // MED-987: scope the 1:1 list to this coach server-side via coachId, and
      // page through every result. The previous implementation fetched a single
      // cohort-wide page (server default cap of 25) and filtered client-side by
      // coach_id, so a coach whose participants fell in rows 26+ silently lost
      // them. Passing coachId returns only this coach's participants (and an
      // accurate count); the loop guarantees a coach with >25 of their own still
      // sees all of them.
      const all: OneOnOneParticipant[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await sessionsApi.listOneOnOneParticipants(cohort.id, {
          coachId: user.id,
          page,
          limit: 25,
        });
        all.push(...response.data);
        totalPages = response.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);
      // MED-1350: a partner coach sees check-in rows only, never paid 1:1 rows.
      // With no paid rows left, the badge counts attended check-ins.
      setOneOnOneParticipants(
        isPartnerCoach
          ? all
              .map((p) => ({
                ...p,
                sessions: p.sessions.filter((s) => isCheckinOneOnOneType(s.one_on_one_type)),
                completed_count: 0,
                total_session_count: 0,
              }))
              .filter((p) => p.sessions.length > 0)
          : all,
      );
    } catch {
      // silent
    } finally {
      setOneOnOneLoading(false);
      setOneOnOneLoaded(true);
    }
  }, [cohort.id, user?.id, isPartnerCoach]);

  // MED-626: Load workshops for coach
  const loadWorkshops = useCallback(async () => {
    setWorkshopsLoading(true);
    try {
      const data = await coachPortalApi.getWorkshops(cohort.id);
      setWorkshops(data);
    } catch {
      // silent — empty state will show
    } finally {
      setWorkshopsLoading(false);
    }
  }, [cohort.id]);

  // MED-1350: every coach loads the list. A partner coach loads it for its
  // check-in rows, also with both toggles off, because a booked check-in
  // stays after the toggles go off.
  useEffect(() => {
    loadOneOnOneParticipants();
  }, [loadOneOnOneParticipants]);

  useEffect(() => {
    loadWorkshops();
  }, [loadWorkshops]);

  // MED-1350: the server lets a coach edit only a session they coach
  // (sessions.coach_id) or a session of a group they coach. Same rule as
  // isSessionCoach in middleware/cohortAccess.ts. cohort.groups holds only the
  // groups this coach leads.
  const canEditSession = useCallback(
    (session: { coach_id?: string | null; group_id?: string | null }) =>
      !!user?.id &&
      (session.coach_id === user.id ||
        (!!session.group_id && cohort.groups.some((g) => g.id === session.group_id))),
    [user?.id, cohort.groups],
  );

  // MED-1360: open a requested 1:1 in the same drawer as its block.
  useEffect(() => {
    if (!openOneOnOneSessionId || !oneOnOneLoaded || oneOnOneLoading) return;
    const participant = oneOnOneParticipants.find((p) =>
      p.sessions.some((s) => s.id === openOneOnOneSessionId),
    );
    if (participant) {
      setSelectedOneOnOneParticipant(participant);
      setSelectedOneOnOneSessionId(openOneOnOneSessionId);
      setOneOnOneOpenedForAttendance(true);
      setOneOnOneDetailOpened(true);
    } else {
      notifications.show({
        title: 'Session not found',
        message: 'This 1:1 session is not in your 1:1 list. Refresh the page and try again.',
        color: 'red',
      });
    }
    onOneOnOneSessionOpened?.();
  }, [
    openOneOnOneSessionId,
    oneOnOneLoaded,
    oneOnOneLoading,
    oneOnOneParticipants,
    onOneOnOneSessionOpened,
  ]);

  const handleOneOnOneSessionClick = useCallback(async (participant: OneOnOneParticipant, session: OneOnOneSessionSlot) => {
    // MED-1350: a session the coach cannot edit opens the read-only drawer,
    // never the date editor. The list also hides the click on such a block.
    if (session.status === 'unscheduled' && canEditSession(session)) {
      // Fetch full session to open edit modal
      try {
        const fullSession = await sessionsApi.getById(cohort.id, session.id);
        setEditOneOnOneSession({ session: fullSession, participant });
      } catch {
        // fallback to detail drawer
        setSelectedOneOnOneParticipant(participant);
        setSelectedOneOnOneSessionId(session.id);
        setOneOnOneDetailOpened(true);
      }
    } else {
      setSelectedOneOnOneParticipant(participant);
      setSelectedOneOnOneSessionId(session.id);
      setOneOnOneDetailOpened(true);
    }
  }, [cohort.id, canEditSession]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const allGroupsExpanded = cohort.groups.length > 0 && cohort.groups.every((g) => expandedGroups.has(g.id));

  const handleExpandCollapseAll = useCallback(() => {
    if (allGroupsExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(cohort.groups.map((g) => g.id)));
    }
  }, [allGroupsExpanded, cohort.groups]);

  // Group huddles by group_id
  const huddlesByGroup = huddles.reduce<Record<string, CoachHuddle[]>>((acc, h) => {
    if (!acc[h.group_id]) acc[h.group_id] = [];
    acc[h.group_id].push(h);
    return acc;
  }, {});

  // MED-1106 AC-B1/B3: gate the modality tabs to the cohort's enabled modalities,
  // derived from `cohort.modalities` — the same source of truth the CM Sessions
  // tab uses (mirrors `useSessionsData`'s hasGroupCoaching/hasOneOnOne/hasWorkshop).
  const enabledModalities = cohort.modalities ?? [];
  const hasGroupCoaching = enabledModalities.includes('group_coaching');
  const hasOneOnOneModality = enabledModalities.includes('one_on_one');
  // MED-1350: check-ins are 1:1 rows, so the 1:1 filter and section show
  // whenever a check-in is on, also for a cohort with group coaching only.
  // A partner coach sees the check-ins only, so the 1:1 modality alone does
  // not show the section for a partner coach.
  // A booked check-in kept after both toggles go off still shows, so the
  // loaded list's live check-in rows count too.
  const hasCheckinRows = oneOnOneParticipants.some((p) =>
    p.sessions.some((s) => isCheckinOneOnOneType(s.one_on_one_type)),
  );
  const hasOneOnOne = isPartnerCoach
    ? cohort.checkins_enabled || hasCheckinRows
    : hasOneOnOneModality || cohort.checkins_enabled || hasCheckinRows;
  const hasWorkshop = enabledModalities.includes('workshop');

  // Filter toggle options. "All" always shows (AC-B1); each modality tab shows
  // only when enabled. A partner coach gets 1:1 for check-ins only (MED-1350).
  const filterOptions: ButtonToggleOption<SectionFilter>[] = useMemo(() => {
    const options: ButtonToggleOption<SectionFilter>[] = [{ value: 'all', label: 'All' }];
    if (hasGroupCoaching) {
      options.push({ value: 'groupCoaching', label: 'Group Coaching', icon: Users });
    }
    if (hasWorkshop) {
      options.push({ value: 'workshops', label: 'Workshops', icon: Layers });
    }
    if (hasOneOnOne) {
      options.push({ value: 'oneOnOnes', label: '1:1s' });
    }
    return options;
  }, [hasGroupCoaching, hasWorkshop, hasOneOnOne]);

  // Section visibility — combine the enabled-modality flag with the active filter
  // so the "All" view never renders a disabled modality's (empty) section.
  const showCohortEvents = true;
  const showGroupCoaching =
    hasGroupCoaching && (activeFilter === 'all' || activeFilter === 'groupCoaching');
  const showWorkshops = hasWorkshop && (activeFilter === 'all' || activeFilter === 'workshops');
  const showOneOnOnes = hasOneOnOne && (activeFilter === 'all' || activeFilter === 'oneOnOnes');

  // MED-1027: the 1:1 "sessions each" header count now comes from the cohort's
  // configured 1:1 modality session count, exactly mirroring the admin/CM view
  // (`modalities.one_on_one.session_count`). The previous source —
  // `cohort.groups[0]?.sessions?.length` — was the group-coaching count, which
  // is only coincidentally right when the group and 1:1 counts happen to match.
  // Note: deriving from `oneOnOneParticipants[0].total_session_count` was
  // rejected because per-participant 1:1 sessions can be non-uniform/partially
  // generated, so the first participant's count is not reliably the configured
  // total (and would diverge from the admin view).
  const oneOnOneSessionCount = cohort.one_on_one_session_count ?? 2;
  // MED-1360: the coach API sends no check-in toggles, so count the check-in
  // types the loaded rows hold. The staff page counts the toggles instead.
  const oneOnOneCheckinCount = new Set(
    oneOnOneParticipants.flatMap((p) =>
      p.sessions
        .map((s) => s.one_on_one_type)
        .filter((t) => isCheckinOneOnOneType(t)),
    ),
  ).size;

  // MED-1354: the group cards hold the "Booking links" button. When they are
  // not on screen (no group coaching, the 1:1s filter, or the by-session view),
  // the 1:1 section shows it instead, so a coach can always reach the 1:1 links.
  const groupCardsShown =
    showGroupCoaching && groupingMode === 'date' && cohort.groups.length > 0;
  const oneOnOneBookingLinks =
    !groupCardsShown && cohort.booking_links_available !== false && cohort.groups.length > 0
      ? cohort.groups.map((group) => (
          <GroupBookingLinks
            key={group.id}
            groupId={group.id}
            groupName={group.name}
            label={cohort.groups.length > 1 ? `Booking links: ${group.name}` : undefined}
            context="oneOnOne"
            showGroupName={cohort.groups.length > 1}
          />
        ))
      : null;

  // MED-626: Handle workshop card click → open detail drawer
  const handleWorkshopClick = useCallback((workshop: CoachWorkshop) => {
    setDetailWorkshop(workshop);
    setDetailDrawerOpen(true);
  }, []);

  return (
    <Stack gap="lg" className="med-coach-sessions">
      {/* Filter toggles + Search bar */}
      <Flex gap="sm" align="center">
        <ButtonToggle
          options={filterOptions}
          value={activeFilter}
          onChange={setActiveFilter}
        />
        <TextInput
          placeholder="Search..."
          leftSection={<Search size={14} />}
          size="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 0 }}
          styles={{ input: { height: 38, borderRadius: 6 } }}
        />
        {/* MED-702: Coach read-only by-session view */}
        <Box w={170} style={{ flexShrink: 0 }}>
          <Select
            data={[
              { value: 'date', label: 'View by Date' },
              { value: 'session', label: 'View by Session' },
            ]}
            value={groupingMode}
            onChange={(v) => v && setGroupingMode(v as 'date' | 'session')}
            size="sm"
            allowDeselect={false}
            styles={{ input: { height: 38, borderRadius: 6 } }}
          />
        </Box>
      </Flex>

      {/* Cohort Events (E8-14) - always visible */}
      {showCohortEvents && (
        <CoachCohortEventsSection cohortId={cohort.id} />
      )}

      {/* Group Coaching Sessions + Huddles */}
      {showGroupCoaching && (
        <div>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Title order={4}>Group Coaching</Title>
              {cohort.groups.length > 0 && (
                <Text size="sm" c="dimmed">
                  {/* MED-1027 (same-class fix): per-group session count from the
                      group-coaching modality config, mirroring the admin view.
                      Was `cohort.session_count`, the cohort-wide aggregate
                      (group + 1:1 + workshops), which over-counted "sessions each". */}
                  {cohort.groups.length} group{cohort.groups.length !== 1 ? 's' : ''} · {cohort.group_session_count || 6} sessions each
                </Text>
              )}
            </Group>
            <Group gap="sm">
              {cohort.groups.length > 0 && !isPartnerCoach && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<Plus size={12} />}
                  onClick={onAddHuddle}
                >
                  {/* MED-1350: this makes a general huddle, not an intro or
                      midpoint check-in. */}
                  Add huddle
                </Button>
              )}
              {cohort.groups.length > 1 && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={handleExpandCollapseAll}
                >
                  {allGroupsExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
            </Group>
          </Group>
          {huddlesLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : groupingMode === 'session' && cohort.groups.length > 0 ? (
            <GroupCoachingBySessionView
              cohortId={cohort.id}
              coachUserId={user?.id}
              readOnly
              huddles={huddles}
              totalParticipants={cohort.groups.reduce(
                (n, g) => n + (g.participant_count ?? 0),
                0,
              )}
              onSessionClick={onSessionClick}
              onHuddleClick={onHuddleClick}
              // MED-1181 / AC2: cohort-level per-modality LSD toggle gates the
              // peer-leader column in the by-session view.
              modalityLiveSessionDeliveryEnabled={cohort.live_session_delivery_enabled}
            />
          ) : cohort.groups.length > 0 ? (
            cohort.groups.map((group) => (
              <GroupSessionsCard
                key={group.id}
                cohortId={cohort.id}
                group={group}
                expanded={expandedGroups.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
                onSessionClick={onSessionClick}
                huddles={huddlesByGroup[group.id] || []}
                onHuddleClick={onHuddleClick}
                onEditHuddle={onEditHuddle}
                onCancelHuddle={onCancelHuddle}
                searchQuery={searchQuery}
                onOpenSurveyResults={onOpenSurveyResults}
                onOpenCoachFeedback={onOpenCoachFeedback}
                isPartnerCoach={isPartnerCoach}
                showOneOnOnes={showOneOnOnes}
                liveSessionDeliveryModalityEnabled={cohort.live_session_delivery_enabled}
                showBookingLinks={cohort.booking_links_available !== false}
              />
            ))
          ) : (
            <Card withBorder p="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <Users size={24} />
                </ThemeIcon>
                <Text fw={600}>No Groups Assigned</Text>
                <Text size="sm" c="dimmed" ta="center">
                  You haven't been assigned to any groups in this cohort yet.
                </Text>
              </Stack>
            </Card>
          )}
        </div>
      )}

      {/* MED-626: Workshops */}
      {showWorkshops && (
        <div>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Title order={4}>Workshops</Title>
              {workshops.length > 0 && (
                <Text size="sm" c="dimmed">
                  {workshops.length} workshop{workshops.length !== 1 ? 's' : ''}
                </Text>
              )}
            </Group>
          </Group>
          {workshopsLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : workshops.length > 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
              {workshops.map((w) => (
                <CoachWorkshopCard
                  key={w.id}
                  workshop={w}
                  onClick={handleWorkshopClick}
                />
              ))}
            </SimpleGrid>
          ) : (
            <Box py="xl">
              <Text c="dimmed" ta="center">No workshops scheduled</Text>
            </Box>
          )}
        </div>
      )}

      {/* 1:1 Coaching Section. MED-1350: a partner coach sees check-in rows
          only, read-only, as the old intro section gave it no actions. */}
      {showOneOnOnes && (
        // MED-1350: the loader shows on the first load only. A reload after a
        // date save keeps the section mounted, so the list or card choice
        // stays and the page does not collapse and jump to the top.
        oneOnOneLoading && oneOnOneParticipants.length === 0 ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : groupingMode === 'session' ? (
          <Box style={{ border: '1px solid #E5E2DC', borderRadius: 8, overflow: 'hidden' }}>
            <Group
              gap="sm"
              style={{
                backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
                padding: '16px 20px',
                borderBottom: '1px solid #E5E2DC',
              }}
              justify="space-between"
              wrap="wrap"
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon color="orange" size={40} radius="md" variant="filled">
                  <User size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={600} size="16px" style={{ whiteSpace: 'nowrap' }}>
                    {/* MED-1350: titled "Check-ins" where the cohort sells no 1:1s. */}
                    {hasOneOnOneModality && !isPartnerCoach ? '1:1 Coaching' : 'Check-ins'}
                  </Text>
                  <Text size="13px" c="dimmed" mt={4}>
                    {oneOnOneParticipants.length} participant{oneOnOneParticipants.length !== 1 ? 's' : ''}
                  </Text>
                </div>
              </Group>
              {oneOnOneBookingLinks && (
                <Group gap="sm" wrap="wrap" justify="flex-end" ml="auto" maw="100%">
                  {oneOnOneBookingLinks}
                </Group>
              )}
            </Group>
            <Box p="md" style={{ backgroundColor: '#FFFFFF' }}>
              <OneOnOneBySessionView
                cohortId={cohort.id}
                coachUserId={user?.id}
                readOnly
                checkinsOnly={isPartnerCoach}
                onSessionClick={isPartnerCoach ? undefined : onSessionClick}
              />
            </Box>
          </Box>
        ) : (
          <OneOnOneCoachingSection
            participants={oneOnOneParticipants}
            sessionCount={oneOnOneSessionCount}
            checkinsOnly={!hasOneOnOneModality || isPartnerCoach}
            checkinCount={oneOnOneCheckinCount}
            useAccordion={activeFilter === 'all'}
            isExpanded
            // MED-1350: "Show N more" opens the 1:1s tab, as the admin view does.
            // It had no handler here, so the button did nothing.
            onShowMore={() => setActiveFilter('oneOnOnes')}
            onSessionClick={isPartnerCoach ? undefined : handleOneOnOneSessionClick}
            canEditSession={canEditSession}
            headerActions={oneOnOneBookingLinks}
            viewMode={oneOnOneViewMode}
            onViewModeChange={setOneOnOneViewMode}
          />
        )
      )}

      {/* E18-3: 1:1 Session Detail Drawer (hidden for partner coaches) */}
      {(!isPartnerCoach || oneOnOneOpenedForAttendance) &&
        selectedOneOnOneParticipant &&
        selectedOneOnOneSessionId && (
        <OneOnOneSessionDetailDrawer
          opened={oneOnOneDetailOpened}
          onClose={() => {
            setOneOnOneDetailOpened(false);
            setOneOnOneOpenedForAttendance(false);
          }}
          cohortId={cohort.id}
          sessionId={selectedOneOnOneSessionId}
          participantName={`${selectedOneOnOneParticipant.first_name} ${selectedOneOnOneParticipant.last_name}`}
          participantAvatarUrl={selectedOneOnOneParticipant.avatar_url}
          coachName={selectedOneOnOneParticipant.coach_name}
          coachAvatarUrl={selectedOneOnOneParticipant.coach_avatar_url}
          totalSessions={selectedOneOnOneParticipant.total_session_count}
          onComplete={() => {
            loadOneOnOneParticipants();
            onOneOnOneAttendanceChanged?.();
          }}
          // MED-1350: same owner rule as the list blocks.
          canEditDate={canEditSession}
          // MED-1360: the same rule decides attendance and notes. Another
          // coach's 1:1 opens read-only.
          canActOnSession={canEditSession}
          resourcesReadOnly
          // MED-1350: date edit from a dated block. Partner coaches have no
          // Edit Session modal, so they get no Edit date either.
          // MED-1394: a withdrawn participant gets no booking actions.
          onEditDate={
            isPartnerCoach || selectedOneOnOneParticipant.enrollment_status === 'withdrawn'
              ? undefined
              : (fullSession) => {
                  setOneOnOneDetailOpened(false);
                  setEditOneOnOneSession({
                    session: fullSession,
                    participant: selectedOneOnOneParticipant,
                  });
                }
          }
        />
      )}

      {/* E18-3: Edit 1:1 Session Modal (hidden for partner coaches) */}
      {!isPartnerCoach && editOneOnOneSession && (
        <EditSessionModal
          opened={editOneOnOneSession !== null}
          onClose={() => setEditOneOnOneSession(null)}
          session={editOneOnOneSession.session}
          cohort={{
            id: cohort.id,
            default_meeting_link: cohort.default_meeting_link,
            default_meeting_platform: cohort.default_meeting_platform,
            // MED-1350: the cohort's 1:1 length, so a row with no
            // duration_minutes defaults its end time as the staff page does.
            modalities: [
              {
                modality_type: 'one_on_one',
                session_duration_minutes: cohort.one_on_one_session_duration_minutes ?? null,
              },
            ],
          }}
          isOneOnOne
          participantName={`${editOneOnOneSession.participant.first_name} ${editOneOnOneSession.participant.last_name}`}
          // MED-1350: the server lets a coach clear only a session they coach or
          // a session of a group they coach, the same rule as Edit date.
          canClearDate={canEditSession(editOneOnOneSession.session)}
          onUpdate={() => {
            setEditOneOnOneSession(null);
            loadOneOnOneParticipants();
          }}
        />
      )}

      {/* MED-626: Workshop Detail Drawer (reuses admin component) */}
      <WorkshopDetailDrawer
        opened={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        cohortId={cohort.id}
        workshop={detailWorkshop as unknown as WorkshopWithSession | null}
        sequential={false}
        onWorkshopUpdated={loadWorkshops}
        canManageResources={false}
      />
    </Stack>
  );
}
